import { Component, effect, forwardRef, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	ControlValueAccessor,
	FormBuilder,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validator,
	Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../../../environments/environment.interface';
import { CreateCareerStatusDynamicFields } from '../../../../../../api/app-types/career.app';
import { LvrField } from '../../../../../../core/components/field/field';
import { LvrSectionForm } from '../../../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../../../core/directives/lvr-input.directive';
import { hasError } from '../../../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../../../shared/padding/padding.directive';
import { ImmediateErrorStateMatcher } from '../../../../../../shared/validators/immediate-error-state-matcher';

@Component({
	selector: 'lvr-social-security-status-minimal',
	templateUrl: './social-security-status-minimal.html',
	styleUrl: './social-security-status-minimal.scss',
	imports: [
		LvrInputDirective,
		TranslocoPipe,
		MatInput,
		MatFormFieldModule,
		LvrField,
		ReactiveFormsModule,
		LvrPaddingDirective,
		LvrSectionForm,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SocialSecurityStatusMinimal),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => SocialSecurityStatusMinimal),
			multi: true,
		},
	],
})
export class SocialSecurityStatusMinimal implements ControlValueAccessor, Validator {
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly env = inject(ENVIRONMENT);
	public readonly form = inject(FormBuilder).group({
		remark: ['', [Validators.maxLength(this.env.formConfiguration.career.remarkLength)]],
	});
	private readonly formValueChanges = toSignal(this.form.valueChanges, { initialValue: this.form.value });

	// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onChange = (_value: CreateCareerStatusDynamicFields | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onTouched = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onValidationChange = () => {};

	// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
	private readonly value = signal<CreateCareerStatusDynamicFields | null>(null);
	private readonly disabled = signal(false);

	public constructor() {
		effect(() => {
			const value = this.formValueChanges() as CreateCareerStatusDynamicFields;
			this.value.set(value);
			this._onChange(value);
		});

		// Trigger validation updates when form status changes
		effect(() => {
			this.formValueChanges();
			this._onValidationChange();
		});
	}

	// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
	public writeValue(obj: CreateCareerStatusDynamicFields | null) {
		this.value.set(obj);
		this._onChange(obj);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.form.patchValue(obj as any, { emitEvent: false });
	}
	public registerOnChange(fn: unknown) {
		// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
		this._onChange = fn as (_value: CreateCareerStatusDynamicFields | null) => void;
	}
	public registerOnTouched(fn: unknown) {
		this._onTouched = fn as () => void;
	}
	public setDisabledState(isDisabled: boolean) {
		this.disabled.set(isDisabled);
	}
	// Validator interface implementation
	public validate(_control: AbstractControl): ValidationErrors | null {
		return this.form.valid ? null : { invalidForm: { valid: false, errors: this.form.errors } };
	}

	public registerOnValidatorChange(fn: () => void): void {
		this._onValidationChange = fn;
	}
}
